import {
  businessSchema,
  objectIdSchema,
  paginationWithSearchSchema,
  productSchema,
  referralCodeLengthSchema,
  referralCodeSchema,
  ReferralLink,
  ReferralLinkSchema,
  statusCommonSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';

/* ─────────────────────────── public ─────────────────────────── */

/**
 * `GET /referral/link/resolve?code=` — public, unauthenticated. Tells a
 * landing page whether a code is live and which business / product it
 * targets so the page can pick the right CTA and `wa.me` line. Returns
 * no partner data.
 */
export const referralLinkResolveAPISchema = z.object({
  code: referralCodeSchema.describe('The code from `?r=`.'),
});
export type ReferralLinkResolved = {
  /** `false` for unknown, expired or blocked codes. */
  valid: boolean;
  code: string;
  /**
   * The link's target business, when it has one. Absent on an "any
   * business" link — the same code serves every business the partner
   * markets; see `channels`.
   */
  business?: 'hero' | 'wathiqa_plus';
  product?: string;
  /** Partner display name, for a "Referred by X" line. */
  partnerName?: string;
  /** Where the link wants to send people, with `?r=` appended (first channel). */
  url?: string;
  /** `wa.me` prefill for the business line, when it has one (first channel). */
  whatsappUrl?: string;
  /** One entry per business the code serves — the same code everywhere. */
  channels?: ReferralLinkChannel[];
};
export type ReferralLinkResolveAPI = API<
  z.infer<typeof referralLinkResolveAPISchema>,
  ReferralLinkResolved
>;

/* ─────────────────────────── admin ─────────────────────────── */

/** `GET /referral/link/admin-all` — paginated + searchable list of links. */
export const referralLinkGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  partnerId: objectIdSchema.optional().describe("Filter to one partner's links."),
  status: statusCommonSchema.optional().describe('Filter by `ReferralLink.status`.'),
  business: businessSchema.optional().describe('Filter by target business.'),
  product: productSchema.optional().describe('Filter by target product.'),
  code: referralCodeSchema.optional().describe('Exact code lookup.'),
});
export type ReferralLinkGetAllAdminAPI = API<
  z.infer<typeof referralLinkGetAllAdminAPISchema>,
  ReferralLink[]
>;

/** `GET /referral/link/admin-one` — one link by id. */
export const referralLinkGetOneAdminAPISchema = z.object({
  _id: objectIdSchema.describe('`ReferralLink._id`.'),
});
export type ReferralLinkGetOneAdminAPI = API<
  z.infer<typeof referralLinkGetOneAdminAPISchema>,
  ReferralLink
>;

/**
 * `POST /referral/link/admin-create` — create a link under a partner.
 * Leave `code` empty and the backend generates one (`codeLength` picks
 * how long — 6 recommended, 4 for a code people remember); supply it to
 * use a vanity code (must be unique).
 */
export const referralLinkCreateAdminAPISchema = ReferralLinkSchema.data
  .omit({ code: true, stats: true, status: true })
  .extend({
    code: referralCodeSchema
      .optional()
      .describe('Vanity code (4–16 letters / digits, unique). Generated when omitted.'),
    codeLength: referralCodeLengthSchema
      .optional()
      .describe('Length of the generated code when `code` is omitted: 4, 5, 6 (default) or 8.'),
    status: statusCommonSchema.optional().describe('Defaults to `active`.'),
  });
export type ReferralLinkCreateAdminAPI = API<
  z.infer<typeof referralLinkCreateAdminAPISchema>,
  ReferralLink
>;

/** `PATCH /referral/link/admin-update` — partial update by id. `stats` is server-owned and ignored. */
export const referralLinkUpdateAdminAPISchema = ReferralLinkSchema.partial
  .omit({ stats: true, partnerId: true })
  .extend({
    _id: objectIdSchema.describe('`ReferralLink._id` to update.'),
  });
export type ReferralLinkUpdateAdminAPI = API<
  z.infer<typeof referralLinkUpdateAdminAPISchema>,
  ReferralLink
>;

/**
 * `GET /referral/link/admin-share` — the copyable artefacts for a link:
 * web URL, app URL, `wa.me` prefill and the embed snippet for an
 * external site.
 */
export const referralLinkShareAdminAPISchema = z.object({
  _id: objectIdSchema.describe('`ReferralLink._id`.'),
});
/**
 * The share artefacts for one business. A link scoped to a business has
 * exactly one; an "any business" link has one per business its partner
 * markets — the same code on every site and every WhatsApp line.
 */
export type ReferralLinkChannel = {
  business: 'hero' | 'wathiqa_plus';
  /** The business website (or the link's destination on it) with `?r=CODE`. */
  url: string;
  /** `wa.me` prefill for the business line, when it publishes one. */
  whatsappUrl?: string;
  /** `<script>` tag an external site for this business pastes to load `hero-r.js`. */
  embedSnippet: string;
};

export type ReferralLinkShare = {
  code: string;
  /** The dashboard signup URL with the code — one, brand-neutral. */
  appUrl: string;
  channels: ReferralLinkChannel[];
};
export type ReferralLinkShareAdminAPI = API<
  z.infer<typeof referralLinkShareAdminAPISchema>,
  ReferralLinkShare
>;
